/**
 * Rate Benchmarks — cross-source rate comparison hub for auditing and
 * finalizing the Part Rate DB.
 *
 * Tabs:
 *   Overview          import registry + validation summaries per source
 *   Ottav 61075       per-source dashboard (matrix + analytics)
 *   Ottav Latest      per-source dashboard + version diff vs 61075
 *   MCAA              per-source dashboard (consolidated MCAA HR sheet)
 *   Estimating Book   S6/S7 tables with editable factor tables (audit-logged)
 *   Comparison Hub    5-source overlay vs Part Rate DB with AI insight
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { ListenButton } from '../../common/AudioGuide';
import AuditLogModal from '../../common/AuditLogModal';
import { styles, ACCENT, StatCard, Spinner, ErrorBox, fmtInt, apiGet, errMsg } from './rbShared';
import SourceDashboard from './SourceDashboard';
import EstimatingBookDashboard from './EstimatingBookDashboard';
import ComparisonHub from './ComparisonHub';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'OTTAV_61075', label: 'Ottav 61075' },
  { id: 'OTTAV_LATEST', label: 'Ottav Latest' },
  { id: 'MCAA', label: 'MCAA' },
  { id: 'EST_BOOK', label: 'Estimating Book' },
  { id: 'comparison', label: 'Comparison Hub' },
];

export default function RateBenchmarksView() {
  const { instance, accounts } = useMsal();
  const [tab, setTab] = useState('comparison');
  const [sources, setSources] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(false);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const loadSources = useCallback(async () => {
    setError(null);
    try {
      const r = await apiGet(instance, accounts, '/rate-benchmarks/sources');
      setSources(r.data || []);
      setCanEdit(!!r.canEdit);
      setAiAvailable(!!r.aiAvailable);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [instance, accounts]);

  useEffect(() => { loadSources(); }, [loadSources]);

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h2 style={styles.title}>📊 Rate Benchmarks</h2>
        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
          Cross-source labour-hour comparison for finalizing the Part Rate DB
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button style={styles.btnGhost} onClick={() => setShowAudit(true)}>📜 Audit Log</button>
          <button style={styles.btnGhost} onClick={() => setShowHelp(true)}>❓ Help</button>
        </div>
      </div>

      <div style={styles.tabBar}>
        {TABS.map((t) => (
          <button key={t.id} style={styles.tab(tab === t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {error && <ErrorBox error={error} onRetry={loadSources} />}

      {tab === 'overview' && <Overview sources={sources} />}
      {tab === 'OTTAV_61075' && <SourceDashboard source="OTTAV_61075" title="Ottav 61075" />}
      {tab === 'OTTAV_LATEST' && <SourceDashboard source="OTTAV_LATEST" title="Ottav Latest (2026-08-07)" showOttavDiff />}
      {tab === 'MCAA' && <SourceDashboard source="MCAA" title="MCAA (consolidated MCAA HR)" />}
      {tab === 'EST_BOOK' && <EstimatingBookDashboard canEdit={canEdit} />}
      {tab === 'comparison' && <ComparisonHub aiAvailable={aiAvailable} canEdit={canEdit} />}

      {showAudit && (
        <AuditLogModal
          isOpen
          onClose={() => setShowAudit(false)}
          endpoint="/rate-benchmarks/audit-log"
          title="Rate Benchmarks Audit Log"
          accentColor={ACCENT}
        />
      )}

      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}

function Overview({ sources }) {
  if (!sources) return <Spinner label="Loading sources…" />;
  if (!sources.length) {
    return (
      <div style={styles.card}>
        No benchmark sources imported yet. Run <code>node scripts/import-rate-benchmarks.js</code> to load the four reference workbooks.
      </div>
    );
  }
  return (
    <div>
      <div style={styles.statGrid}>
        {sources.map((s) => (
          <StatCard
            key={s.code}
            label={s.label || s.code}
            value={fmtInt(s.entry_count)}
            sub={`${fmtInt(s.part_count)} parts · imported ${s.imported_at ? new Date(s.imported_at).toLocaleDateString() : '—'}`}
          />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 10 }}>
        {sources.map((s) => (
          <div key={s.code} style={styles.card}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{s.label || s.code}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 8 }}>{s.file_name}</div>
            {s.validation ? (
              <ValidationSummary v={s.validation} />
            ) : <div style={{ color: '#9ca3af', fontSize: '0.78rem' }}>No validation report.</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ValidationSummary({ v }) {
  const entries = Object.entries(v).filter(([, val]) => typeof val !== 'object');
  return (
    <table style={{ ...styles.table, fontSize: '0.75rem' }}>
      <tbody>
        {entries.map(([k, val]) => (
          <tr key={k}>
            <td style={{ ...styles.td, color: '#6b7280' }}>{k}</td>
            <td style={styles.tdNum}>{String(val)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HelpModal({ onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 10, maxWidth: 760, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ position: 'sticky', top: 0, background: `linear-gradient(135deg, ${ACCENT} 0%, #115e59 100%)`, color: 'white', padding: '0.9rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>📊 Rate Benchmarks — How it works</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId="rate-benchmarks-help" title="Rate Benchmarks Help" accent={ACCENT} dark />
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
        </div>
        <div style={{ padding: '1.25rem 1.5rem', fontSize: '0.85rem', color: '#374151', lineHeight: 1.6 }}>
          <h4 style={{ margin: '0 0 .35rem' }}>What this is</h4>
          <p style={{ margin: '0 0 .9rem' }}>
            A side-by-side audit of the <strong>Part Rate DB</strong> labour hours against four independent references:
            two <strong>Ottav</strong> rate books (job 61075 and the latest edition), the <strong>MCAA</strong> consolidated
            labour units, and the internal <strong>Estimating Book</strong> (sections six and seven). Every source was
            imported, normalized to one vocabulary (description, material, schedule, rating, size), and joined so each
            part-size shows up as one row across all sources.
          </p>

          <h4 style={{ margin: '0 0 .35rem' }}>Reading the Comparison Hub</h4>
          <p style={{ margin: '0 0 .9rem' }}>
            Each row is one part at one size. A number means the source has an hour value there. A yellow
            <strong> gap</strong> badge means the source knows the part family but has no value at this size —
            a candidate data-entry hole. A grey dash means the source simply doesn't cover that part.
            Superscripts: <strong>f</strong> = value derived from an Estimating Book factor, <strong>*</strong> = matched
            through a schedule wildcard (e.g. flanges keyed by bore schedule on one side only), <strong>~</strong> = averaged
            across schedules. Ratio badges compare each benchmark against its own like-for-like column:
            Ottav and Estimating Book against <em>OTT Hour</em>, MCAA against the Part Rate DB's <em>MCAA</em> column.
          </p>

          <h4 style={{ margin: '0 0 .35rem' }}>AI insight</h4>
          <p style={{ margin: '0 0 .9rem' }}>
            The paragraph at the top of the Comparison Hub is generated by the portal's AI from the aggregated statistics
            of your <em>current filter</em> — change any filter and it re-reads the picture: systematic bias between
            sources, biggest outliers, coverage holes. Only aggregate numbers are sent to the model, never the raw tables.
          </p>

          <h4 style={{ margin: '0 0 .35rem' }}>Estimating Book factors</h4>
          <p style={{ margin: '0 0 .9rem' }}>
            Each Estimating Book sheet carries its base table (usually carbon steel, standard schedule) plus
            multiplier factors (schedule 80, stainless, aluminum…). Factors given as a range show the range and use the
            <strong> midpoint</strong> as the calculation base. Authorized users can edit factors or add new ones — every
            change is audit-logged, and derived rows in the Comparison Hub update immediately (marked <strong>f</strong>).
          </p>

          <h4 style={{ margin: '0 0 .35rem' }}>Unmatched &amp; crosswalk</h4>
          <p style={{ margin: 0 }}>
            The Unmatched tab lists part families that exist in a benchmark but never join a Part Rate DB row (and vice
            versa). Often it's just spelling — add a crosswalk mapping (e.g. "Conc. Swage" → "CONC-SWAGE") and the join
            recomputes. Real scope gaps are your shopping list for extending the Part Rate DB.
          </p>
        </div>
      </div>
    </div>
  );
}
